import { useState, useCallback } from "react";
import { VmCostService } from "../../application/services/vmCostService";

const vmCostService = new VmCostService();

export const useVmCost = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vmCostData, setVmCostData] = useState(null);
  const [index, setIndex] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [months, setMonths] = useState([]);
  const [vmCostHistory, setVmCostHistory] = useState([]);
  const [monthlyCostsRange, setMonthlyCostsRange] = useState(null);
  const [totalCostData, setTotalCostData] = useState(null);

  const getMonthlyCostsByDateRange = useCallback(async (vmId, startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const result = await vmCostService.getMonthlyCostsByDateRange(
        vmId,
        startDate,
        endDate,
      );
      if (result.success) {
        setMonthlyCostsRange(result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const getVmCostHistory = useCallback(async (pageIndex = 0) => {
    setLoading(true);
    setError(null);
    try {
      const result = await vmCostService.getVmCostHistory(pageIndex);
      if (result.success) {
        setVmCostData(result.data);
        setIndex(result.data.index);
        setHasNext(result.data.hasNext);
        setHasPrevious(result.data.hasPrevious);
        setMonths(result.data.months);
        setVmCostHistory(result.data.vmCostHistory);
        return { success: true, data: result.data };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const getTotalCostForVms = useCallback(async (vmIds, startMonth, startYear, endMonth, endYear) => {
    setLoading(true);
    setError(null);
    try {
      const result = await vmCostService.getTotalCostForVms(vmIds, startMonth, startYear, endMonth, endYear);
      if (result.success) {
        setTotalCostData(result.data);
        return { success: true, data: result.data };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (error) {
      setError(error.message);
      return { success: false, message: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const nextPage = useCallback(async () => {
    if (hasNext) {
      const newIndex = index + 1;
      setIndex(newIndex);
      return await getVmCostHistory(newIndex);
    }
    return null;
  }, [hasNext, index, getVmCostHistory]);

  const previousPage = useCallback(async () => {
    if (hasPrevious) {
      const newIndex = index - 1;
      setIndex(newIndex);
      return await getVmCostHistory(newIndex);
    }
    return null;
  }, [hasPrevious, index, getVmCostHistory]);

  const goToPage = useCallback(async (pageIndex) => {
    if (pageIndex >= 0) {
      setIndex(pageIndex);
      return await getVmCostHistory(pageIndex);
    }
    return null;
  }, [getVmCostHistory]);

  return {
    getVmCostHistory,
    getMonthlyCostsByDateRange,
    getTotalCostForVms,
    nextPage,
    previousPage,
    goToPage,
    vmCostData,
    vmCostHistory,
    monthlyCostsRange,
    totalCostData,
    months,
    index,
    hasNext,
    hasPrevious,
    loading,
    error,
  };
};